import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import NotificationUnreadBadge from "./notifications/NotificationUnreadBadge";
import useNotificationUnreadCount from "../hooks/useNotificationUnreadCount";
import useSmoothPageScroll from "../hooks/useSmoothPageScroll";
import { useAuth } from "../store/AuthContext";
import { getHomeRouteByRole, hasAdminControlAccess } from "../utils/roleRoutes";
import { hasAdminModuleAccess } from "../utils/adminPermissions";
import InvestorSidebarNav from "./investor/InvestorSidebarNav";
import InvestorMobileBottomNav from "./investor/InvestorMobileBottomNav";
import { BRAND_LOGO } from "../data/platformPresentation";

function BrandWordmark({ className = "", to = "/" }) {
  return (
    <Link to={to} className={["nh-rh__logo-mark", className].filter(Boolean).join(" ")} aria-label={BRAND_LOGO.ariaLabel}>
      <span className="nh-rh__logo-need-health">{BRAND_LOGO.needHealth}</span>
      <span className="nh-rh__logo-care">{BRAND_LOGO.care}</span>
    </Link>
  );
}
const byRoleLinks = (user) => {
  const role = user?.role;
  const common = [
    { to: getHomeRouteByRole(role, user), label: "Dashboard" },
    { to: "/notifications", label: "Notifications" },
  ];
  if (["INVESTOR", "MEMBER"].includes(role)) {
    return null;
  }
  if (role === "ASSET_MANAGER") {
    return [
      { to: "/dashboard/asset-manager", label: "Dashboard" },
      { to: "/asset-manager/history/pnl", label: "My History" },
      { to: "/asset-manager/projects", label: "Assigned Projects" },
      { to: "/notifications", label: "Notifications" },
    ];
  }
  const adminLinks = [];
  if (hasAdminControlAccess(user)) {
    adminLinks.push({ to: "/admin/control", label: "Admin Control" });
  }
  if (hasAdminModuleAccess(user, "OPERATIONS")) {
    adminLinks.push({ to: "/admin/operations", label: "Admin Ops" });
    adminLinks.push({ to: "/admin/reconciliation", label: "Reconciliation" });
  }
  if (hasAdminModuleAccess(user, "PAYMENTS")) {
    adminLinks.push({ to: "/admin/payments", label: "Admin Payments" });
  }
  if (hasAdminModuleAccess(user, "HISTORY")) {
    adminLinks.push({ to: "/admin/history/deposits", label: "History" });
  }
  if (
    hasAdminModuleAccess(user, "HISTORY")
    && ["ADMIN", "SUPER_ADMIN", "ACCOUNTANT"].includes(String(role || "").toUpperCase())
  ) {
    adminLinks.push({ to: "/admin/audit", label: "Audit trail" });
  }
  if (hasAdminModuleAccess(user, "DASHBOARD")) {
    adminLinks.push({ to: "/admin/financial-report", label: "Financial Report" });
  }
  if (hasAdminModuleAccess(user, "CONTROL")) {
    adminLinks.push({ to: "/admin/users", label: "Users (new signups)" });
  }
  adminLinks.push({ to: "/join", label: "Public signup page" });
  return [
    ...common,
    ...adminLinks,
  ];
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const links = byRoleLinks(user);
  const { unreadCount } = useNotificationUnreadCount();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [staffNavOpen, setStaffNavOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const isInvestor = ["INVESTOR", "MEMBER"].includes(String(user?.role || ""));

  const profileInitial = useMemo(() => {
    const name = String(user?.full_name || "").trim();
    return name ? name.charAt(0).toUpperCase() : "U";
  }, [user?.full_name]);

  const accountNumber = user?.accountNumber || user?.account_number;

  useSmoothPageScroll(isInvestor);

  useEffect(() => {
    setStaffNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!staffNavOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") setStaffNavOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
    };
  }, [staffNavOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setProfileMenuOpen(false);
    };
    window.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, [profileMenuOpen]);

  return (
    <div className={["app-shell", isInvestor ? "app-shell--investor" : ""].filter(Boolean).join(" ")}>
      {!isInvestor ? (
        <header className="staff-mobile-bar">
          <div className="staff-mobile-bar__identity">
            <span className="app-brand">Need Healthcare</span>
            {user?.role ? <small className="app-user">{user.role}</small> : null}
          </div>
          <button
            type="button"
            className="staff-mobile-bar__toggle"
            aria-expanded={staffNavOpen}
            aria-controls="app-sidebar"
            aria-label={staffNavOpen ? "Close menu" : "Open menu"}
            onClick={() => setStaffNavOpen((v) => !v)}
          >
            <span className="staff-mobile-bar__toggle-bar" aria-hidden />
            <span className="staff-mobile-bar__toggle-bar" aria-hidden />
            <span className="staff-mobile-bar__toggle-bar" aria-hidden />
          </button>
        </header>
      ) : null}
      {!isInvestor && staffNavOpen ? (
        <button type="button" className="staff-nav-backdrop" aria-label="Close menu" onClick={() => setStaffNavOpen(false)} />
      ) : null}
      <aside id="app-sidebar" className={["app-sidebar", "card", staffNavOpen ? "app-sidebar--open" : ""].filter(Boolean).join(" ")}>
        <div className="app-sidebar__identity">
          <div className="app-brand">
            {isInvestor ? (
              <BrandWordmark className="app-brand__logo" to="/dashboard/investor" />
            ) : (
              "Need Healthcare"
            )}
          </div>
          <small className="app-user">
            {user?.full_name}
            {user?.role && !isInvestor ? ` (${user.role})` : ""}
          </small>
          {isInvestor && accountNumber ? (
            <small className="app-user app-user--account" style={{ display: "block" }}>
              Ac. No: {accountNumber}
            </small>
          ) : null}
        </div>
        {links === null ? (
          <InvestorSidebarNav />
        ) : (
          <nav className="app-nav app-nav-vertical">
            {links.map((link) => {
              const pathOnly = String(link.to).split("?")[0];
              const active =
                pathOnly === "/admin/control"
                  ? location.pathname === "/admin/control"
                  : pathOnly.startsWith("/admin/history")
                    ? location.pathname.startsWith("/admin/history")
                    : pathOnly.startsWith("/my-history")
                      ? location.pathname.startsWith("/my-history") || location.pathname === "/transactions"
                      : pathOnly.startsWith("/asset-manager")
                        ? location.pathname.startsWith("/asset-manager")
                        : location.pathname === pathOnly;
              const isNotifications = link.to === "/notifications";
              const showUnread = isNotifications && unreadCount > 0;
              return (
                <Link
                  key={link.to}
                  className={[
                    "nav-link",
                    active ? "nav-link--active" : "",
                    showUnread ? "nav-link--has-unread" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  to={link.to}
                >
                  <span className="nav-link__label">{link.label}</span>
                  {showUnread ? (
                    <NotificationUnreadBadge count={unreadCount} className="nav-link__unread-badge" />
                  ) : null}
                </Link>
              );
            })}
          </nav>
        )}
        <button type="button" className="btn-secondary sidebar-logout" onClick={logout}>
          Log out
        </button>
      </aside>

      <main className="app-main app-main-content">
        {isInvestor ? (
          <header className="investor-chrome-bar" ref={profileMenuRef}>
            <div className="investor-chrome-bar__brand">
              <BrandWordmark className="investor-chrome-bar__logo" to="/dashboard/investor" />
              {accountNumber ? <span className="investor-chrome-bar__acct">Ac. {accountNumber}</span> : null}
            </div>
            <button
              type="button"
              className="profile-menu-trigger"
              onClick={() => setProfileMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={profileMenuOpen}
              aria-label="Open profile menu"
            >
              {user?.profile_photo_url ? (
                <img className="profile-menu-avatar" src={user.profile_photo_url} alt="" />
              ) : (
                <span className="profile-menu-avatar profile-menu-avatar-fallback">{profileInitial}</span>
              )}
              <span className="profile-menu-label">Profile</span>
              <span className="profile-menu-caret" aria-hidden="true">
                ▾
              </span>
            </button>
            {profileMenuOpen ? (
              <div className="profile-menu-dropdown card" role="menu" aria-label="Profile menu">
                <Link
                  className="profile-menu-item"
                  to="/profile?section=personal"
                  role="menuitem"
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Personal information
                </Link>
                <Link className="profile-menu-item" to="/kyc" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                  KYC
                </Link>
                <Link
                  className="profile-menu-item"
                  to="/profile?section=settings"
                  role="menuitem"
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Settings
                </Link>
                <Link
                  className="profile-menu-item"
                  to="/profile?section=withdrawal-preset"
                  role="menuitem"
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Withdrawal preset
                </Link>
                <button
                  type="button"
                  className="profile-menu-item profile-menu-item--danger"
                  role="menuitem"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    logout();
                  }}
                >
                  Log out
                </button>
              </div>
            ) : null}
          </header>
        ) : null}

        <div className="app-main__body">{children}</div>
      </main>

      {isInvestor ? <InvestorMobileBottomNav /> : null}
    </div>
  );
}
